import { useContext, useMemo } from 'react';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { CommentCountsContext } from '@confluence/comment-context/entry-points/CommentCountsContext.ts';
import { useSimplifiedNavNewUsers } from '@confluence/experiment-simplify-nav-new-user/entry-points/useSimplifiedNavNewUsers.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useDocumentScrollHeight } from '@confluence/document-scroll-height/entry-points/useDocumentScrollHeight.ts';
import { docHasContent } from '@confluence/read-time/entry-points/docHasContent.ts';

import { FeatureStates } from '../popupStateTypes.ts';

import { useDynamicSplitButton } from './useDynamicSplitButton.ts';

type UseDynamicAIContextReturn = {
	dynamicActionTarget:
		| FeatureStates.Comments
		| FeatureStates.PageCatchup
		| FeatureStates.QuickSummary
		| null;
	queryError?: ApolloErrorInterop;
};

export const useDynamicAIContext = (
	contentId: string,
	contentType: string,
	pageCommentsSectionInView: boolean,
	isLivePage: boolean,
	contentIdFromQuery?: string | null,
	contentBodyDynamic?: Parameters<typeof docHasContent>[0],
): UseDynamicAIContextReturn => {
	const { scrollY } = useDocumentScrollHeight(isLivePage);

	const { unresolvedInlineCommentsCount, footerCommentsCount } = useContext(CommentCountsContext);
	const { isAdminHubAIEnabled } = useSessionData();

	const contentTypeEnum = contentType === 'page' ? 'PAGE' : 'BLOGPOST';
	const { showCatchupAsDefault, queryError } = useDynamicSplitButton(contentId, contentTypeEnum);
	const { navStyle } = useSimplifiedNavNewUsers();
	const isSimplifiedNavExperimentVariant =
		navStyle === 'default-templates' || navStyle === 'pin-templates';

	// contentBodyDynamic is passed through from the view-page query (same ref when data unchanged), so this memo is stable.
	const hasContentToSummarize = useMemo(
		() => (isSimplifiedNavExperimentVariant ? docHasContent(contentBodyDynamic) : true),
		[isSimplifiedNavExperimentVariant, contentBodyDynamic],
	);

	if (!isAdminHubAIEnabled) {
		return { dynamicActionTarget: null };
	}

	if (pageCommentsSectionInView && (unresolvedInlineCommentsCount > 0 || footerCommentsCount > 0))
		return { dynamicActionTarget: FeatureStates.Comments };

	if (showCatchupAsDefault && scrollY !== null && scrollY < 400) {
		return { dynamicActionTarget: FeatureStates.PageCatchup };
	}

	if (scrollY !== null && scrollY < 400) {
		if (isSimplifiedNavExperimentVariant) {
			const canUseSyncBlankCheck =
				contentIdFromQuery != null &&
				contentBodyDynamic !== undefined &&
				contentId === contentIdFromQuery;
			if (canUseSyncBlankCheck && !hasContentToSummarize) {
				return { dynamicActionTarget: null, queryError };
			}
		}

		return { dynamicActionTarget: FeatureStates.QuickSummary, queryError };
	}

	return { dynamicActionTarget: null, queryError };
};
